// @ts-nocheck
import { useState, useCallback } from 'react';
import { UserProfile, UserInventory, StockBatch, IngredientDetail, LogEntry } from '../types';
import { dataService, calculateAge } from '@foodbook/core-logic';

interface UseUserProps {
  initialUser: UserProfile;
  initialInventory: UserInventory;
  onUserChange: (user: UserProfile) => void;
  onInventoryChange: (inventory: UserInventory) => void;
}

interface UseUserReturn {
  // State
  user: UserProfile;
  setUser: (user: UserProfile | ((prev: UserProfile) => UserProfile)) => void;
  inventory: UserInventory;
  setInventory: (inventory: UserInventory | ((prev: UserInventory) => UserInventory)) => void;
  logs: LogEntry[];
  setLogs: (logs: LogEntry[]) => void;

  // Extended Profile State
  basicInfo: {
    nickname?: string;
    avatarUrl?: string;
    gender?: 'male' | 'female' | 'unspecified';
    birthday?: string;
    bio?: string;
  } | null;
  bodyData: {
    height?: number;
    weight?: number;
    targetWeight?: number;
    activityLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
    bmr?: number;
    tdee?: number;
    bmi?: number;
    bmiCategory?: 'underweight' | 'normal' | 'overweight' | 'obese';
    birthdate?: string;
    age?: number;
  } | null;
  dietaryPreferences: {
    tastePreference?: string[];
    cuisinePreference?: string[];
    dislikedIngredients?: string[];
    allergens?: string[];
    dietaryRestrictions?: string[];
    mealFrequency?: number;
    snackPreference?: boolean;
    cookingSkillLevel?: string;
    cookingTools?: string[];
  } | null;
  nutritionGoals: {
    targetCalories?: number;
    targetProtein?: number;
    targetFat?: number;
    targetCarbs?: number;
    targetFiber?: number;
    targetSodium?: number;
    targetSugar?: number;
    goalType?: 'maintain' | 'lose_weight' | 'gain_weight' | 'muscle_building';
  } | null;

  // Handlers
  awardXP: (amount: number) => void;
  handleProfileUpdate: (updates: Partial<UserProfile>, showToastMessage: (msg: string) => void) => void;
  handleToggleTheme: () => void;
  handleSaveBatch: (ingredientId: string, batch: Partial<StockBatch>, ingredients: IngredientDetail[], showToastMessage: (msg: string) => void) => void;
  setFavoriteRecipeIds: (ids: string[]) => void;
  setFavoriteIngredientIds: (ids: string[]) => void;

  // Extended Profile Handlers
  updateBasicInfo: (updates: Partial<{
    nickname: string;
    avatarUrl: string;
    gender: string;
    birthday: string;
    bio: string;
  }>, showToastMessage: (msg: string) => void) => Promise<void>;
  updateBodyData: (data: {
    height?: number;
    weight?: number;
    targetWeight?: number;
    activityLevel?: string;
  }, showToastMessage: (msg: string) => void) => Promise<void>;
  updateDietaryPreferences: (preferences: {
    tastePreference?: string[];
    cuisinePreference?: string[];
    dislikedIngredients?: string[];
    allergens?: string[];
    dietaryRestrictions?: string[];
    mealFrequency?: number;
    snackPreference?: boolean;
    cookingSkillLevel?: string;
  }, showToastMessage: (msg: string) => void) => Promise<void>;
  updateNutritionGoals: (goals: {
    targetCalories?: number;
    targetProtein?: number;
    targetFat?: number;
    targetCarbs?: number;
    targetFiber?: number;
    goalType?: string;
  }, showToastMessage: (msg: string) => void) => Promise<void>;
  loadUserProfile: (userId: string) => Promise<void>;
}

export const useUser = ({ initialUser, initialInventory, onUserChange, onInventoryChange }: UseUserProps): UseUserReturn => {
  const [user, setUserInternal] = useState<UserProfile>(initialUser);
  const [inventory, setInventoryInternal] = useState<UserInventory>(initialInventory);
  const [logs, setLogs] = useState<LogEntry[]>([]);

  // Extended profile state
  const [basicInfo, setBasicInfo] = useState<UseUserReturn['basicInfo']>(null);
  const [bodyData, setBodyData] = useState<UseUserReturn['bodyData']>(null);
  const [dietaryPreferences, setDietaryPreferences] = useState<UseUserReturn['dietaryPreferences']>(null);
  const [nutritionGoals, setNutritionGoals] = useState<UseUserReturn['nutritionGoals']>(null);

  // Sync with external user when it changes
  const setUser = useCallback((newUser: UserProfile | ((prev: UserProfile) => UserProfile)) => {
    setUserInternal(prev => {
      const updated = typeof newUser === 'function' ? newUser(prev) : newUser;
      onUserChange(updated);
      return updated;
    });
  }, [onUserChange]);

  // Sync with external inventory when it changes
  const setInventory = useCallback((newInventory: UserInventory | ((prev: UserInventory) => UserInventory)) => {
    setInventoryInternal(prev => {
      const updated = typeof newInventory === 'function' ? newInventory(prev) : newInventory;
      onInventoryChange(updated);
      return updated;
    });
  }, [onInventoryChange]);

  // Set favorite recipe IDs
  const setFavoriteRecipeIds = useCallback((ids: string[]) => {
    setUser(prev => ({ ...prev, favoriteRecipeIds: ids }));
  }, [setUser]);

  // Set favorite ingredient IDs
  const setFavoriteIngredientIds = useCallback((ids: string[]) => {
    setUser(prev => ({ ...prev, favoriteIngredientIds: ids }));
  }, [setUser]);

  // Award XP
  const awardXP = useCallback((amount: number) => {
    setUser(prev => {
      const newXp = prev.xp + amount;
      if (newXp >= prev.maxXp) {
        return { ...prev, level: prev.level + 1, xp: newXp - prev.maxXp, maxXp: Math.round(prev.maxXp * 1.2) };
      }
      return { ...prev, xp: newXp };
    });
  }, [setUser]);

  // Handle Profile Update (增强版 - 支持BMI和偏好数据)
  const handleProfileUpdate = useCallback((
    updates: Partial<UserProfile>,
    showToastMessage: (msg: string) => void,
    additionalData?: {
      gender?: 'male' | 'female' | 'unspecified';
      birthdate?: string;
      cookingTools?: string[];
      activityLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
      goalType?: 'maintain' | 'lose_weight' | 'gain_weight' | 'muscle_building';
      cuisinePreferences?: string[];
      tastePreferences?: string[];
      // 身体数据计算值
      bmi?: number;
      bmiCategory?: 'underweight' | 'normal' | 'overweight' | 'obese';
      bmr?: number;
      tdee?: number;
    }
  ) => {
    setUser(prev => {
      const updated: UserProfile = {
        ...prev,
        ...updates,
        // 身体数据
        gender: additionalData?.gender ?? prev.gender,
        birthdate: additionalData?.birthdate ?? prev.birthdate,
        bmi: additionalData?.bmi ?? prev.bmi,
        bmiCategory: additionalData?.bmiCategory ?? prev.bmiCategory,
        bmr: additionalData?.bmr ?? prev.bmr,
        tdee: additionalData?.tdee ?? prev.tdee,
        // 偏好数据
        cookingTools: additionalData?.cookingTools ?? prev.cookingTools,
        activityLevel: additionalData?.activityLevel ?? prev.activityLevel,
        goalType: additionalData?.goalType ?? prev.goalType,
        cuisinePreferences: additionalData?.cuisinePreferences ?? prev.cuisinePreferences,
        tastePreferences: additionalData?.tastePreferences ?? prev.tastePreferences,
        // 更新 goals
        goals: updates.goals ?? prev.goals,
        updatedAt: new Date().toISOString()
      };

      // 同步更新本地状态
      setBasicInfo(b => b ? { ...b, gender: additionalData?.gender } : { gender: additionalData?.gender });
      setBodyData(b => b ? {
        ...b,
        height: updated.goals?.height,
        weight: updated.goals?.weight,
        activityLevel: updated.activityLevel,
        bmi: updated.bmi,
        bmiCategory: updated.bmiCategory,
        bmr: updated.bmr,
        tdee: updated.tdee,
        birthdate: updated.birthdate,
        age: additionalData?.birthdate ? calculateAge(additionalData.birthdate) : undefined
      } : {
        height: updated.goals?.height,
        weight: updated.goals?.weight,
        activityLevel: updated.activityLevel,
        bmi: updated.bmi,
        bmiCategory: updated.bmiCategory,
        bmr: updated.bmr,
        tdee: updated.tdee,
        birthdate: updated.birthdate,
        age: additionalData?.birthdate ? calculateAge(additionalData.birthdate) : undefined
      });
      setDietaryPreferences(d => d ? {
        ...d,
        cookingTools: updated.cookingTools,
        cuisinePreference: updated.cuisinePreferences,
        tastePreference: updated.tastePreferences
      } : {
        cookingTools: updated.cookingTools,
        cuisinePreference: updated.cuisinePreferences,
        tastePreference: updated.tastePreferences
      });
      setNutritionGoals(n => n ? {
        ...n,
        goalType: updated.goalType,
        targetCalories: updated.goals?.targetCalories,
        targetProtein: updated.goals?.targetProtein,
        targetFat: updated.goals?.targetFat,
        targetCarbs: updated.goals?.targetCarbs,
        targetFiber: updated.goals?.targetFiber
      } : {
        goalType: updated.goalType,
        targetCalories: updated.goals?.targetCalories,
        targetProtein: updated.goals?.targetProtein,
        targetFat: updated.goals?.targetFat,
        targetCarbs: updated.goals?.targetCarbs,
        targetFiber: updated.goals?.targetFiber
      });

      // 保存到 DataService
      const saveData = {
        bmi: updated.bmi,
        bmiCategory: updated.bmiCategory,
        bmr: updated.bmr,
        tdee: updated.tdee,
        gender: updated.gender,
        birthdate: updated.birthdate,
        cookingTools: updated.cookingTools,
        activityLevel: updated.activityLevel,
        goalType: updated.goalType,
        cuisinePreferences: updated.cuisinePreferences,
        tastePreferences: updated.tastePreferences,
        goals: updated.goals
      };
      
      console.log('[useUser] 正在保存配置:', saveData);
      
      dataService.saveUserPreferences(saveData).then(() => {
        console.log('[useUser] 配置保存成功');
        showToastMessage('配置已保存');
      }).catch((error) => {
        console.error('[useUser] 保存配置失败:', error);
        console.error('[useUser] 错误详情:', {
          message: error.message,
          code: error.code,
          type: error.constructor.name,
          stack: error.stack
        });
        showToastMessage(`保存失败: ${error.message || '请检查网络连接'}`);
      });

      return updated;
    });
  }, [setUser]);

  // Handle Theme Toggle
  const handleToggleTheme = useCallback(() => {
    setUser(prev => ({
      ...prev,
      theme: prev.theme === 'light' ? 'dark' : 'light'
    }));
  }, [setUser]);

  // Handle Save Batch
  const handleSaveBatch = useCallback((ingredientId: string, batch: Partial<StockBatch>, ingredients: IngredientDetail[], showToastMessage: (msg: string) => void) => {
    setInventory(prev => {
      const newInventory = { ...prev };

      if (!newInventory[ingredientId]) {
        const ingredient = ingredients.find(ing => ing.id === ingredientId);
        newInventory[ingredientId] = {
          currentAmount: 0,
          requiredAmount: 0,
          unit: ingredient?.unit || 'g',
          batches: []
        };
      }

      const inventoryItem = newInventory[ingredientId];

      if (batch.id) {
        const batchIndex = inventoryItem.batches.findIndex(b => b.id === batch.id);
        if (batchIndex !== -1) {
          const oldQuantity = inventoryItem.batches[batchIndex].quantity;
          
          // 重新计算 daysLeft（如果 expirationDate 被更新）
          let daysLeft = inventoryItem.batches[batchIndex].daysLeft;
          if (batch.expirationDate) {
            daysLeft = Math.ceil((new Date(batch.expirationDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
          }
          
          inventoryItem.batches[batchIndex] = {
            ...inventoryItem.batches[batchIndex],
            ...batch,
            daysLeft
          } as StockBatch;

          const quantityDiff = (batch.quantity || oldQuantity) - oldQuantity;
          inventoryItem.currentAmount += quantityDiff;
        }
      } else {
        const expirationDate = batch.expirationDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const daysLeft = Math.ceil((new Date(expirationDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
        
        const newBatch: StockBatch = {
          id: `batch-${Date.now()}-${ingredientId}`,
          date: batch.date || new Date().toISOString().split('T')[0],
          expirationDate: expirationDate,
          quantity: batch.quantity || 1,
          unit: batch.unit || inventoryItem.unit,
          location: batch.location || '冰箱冷藏',
          daysLeft: daysLeft,
          note: batch.note
        };

        inventoryItem.batches.push(newBatch);
        inventoryItem.currentAmount += newBatch.quantity;
      }

      return newInventory;
    });
    showToastMessage('库存已更新');
  }, [setInventory]);

  // Load user profile data
  const loadUserProfile = useCallback(async (userId: string) => {
    try {
      const [basic, body, dietary, goals] = await Promise.all([
        dataService.getUserBasicInfo(userId),
        dataService.getUserBodyData(userId),
        dataService.getUserDietaryPreferences(userId),
        dataService.getUserNutritionGoals(userId)
      ]);

      setBasicInfo(basic || null);
      setBodyData(body || null);
      setDietaryPreferences(dietary || null);
      setNutritionGoals(goals || null);
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  }, []);

  // Update basic info
  const updateBasicInfo = useCallback(async (updates: Partial<{
    nickname: string;
    avatarUrl: string;
    gender: string;
    birthday: string;
    bio: string;
  }>, showToastMessage: (msg: string) => void) => {
    try {
      const userId = user.id;
      await dataService.updateUserBasicInfo(userId, updates);
      setBasicInfo(prev => prev ? { ...prev, ...updates } : updates as any);
      showToastMessage('基本信息已更新');
    } catch (error) {
      console.error('Failed to update basic info:', error);
      showToastMessage('更新失败，请重试');
    }
  }, [user.id]);

  // Update body data
  const updateBodyData = useCallback(async (data: {
    height?: number;
    weight?: number;
    targetWeight?: number;
    activityLevel?: string;
  }, showToastMessage: (msg: string) => void) => {
    try {
      const userId = user.id;

      // 计算 BMR 和 TDEE
      let bmr = 0;
      let tdee = 0;

      if (data.weight && data.height) {
        // 简化 BMR 计算（使用 Mifflin-St Jeor 公式，假设女性作为默认值）
        const gender = basicInfo?.gender === 'male' ? 'male' : 'female';
        if (gender === 'male') {
          bmr = Math.round(10 * data.weight + 6.25 * data.height - 5 * 30 + 5);
        } else {
          bmr = Math.round(10 * data.weight + 6.25 * data.height - 5 * 30 - 161);
        }
      }

      // 活动系数
      const activityMultipliers: Record<string, number> = {
        sedentary: 1.2,
        light: 1.375,
        moderate: 1.55,
        active: 1.725,
        very_active: 1.9
      };

      if (bmr && data.activityLevel) {
        tdee = Math.round(bmr * (activityMultipliers[data.activityLevel] || 1.2));
      }

      await dataService.updateUserBodyData(userId, { ...data, bmr, tdee });
      setBodyData(prev => prev ? { ...prev, ...data, bmr, tdee } : { userId, ...data, bmr, tdee } as any);
      showToastMessage('身体数据已更新');
    } catch (error) {
      console.error('Failed to update body data:', error);
      showToastMessage('更新失败，请重试');
    }
  }, [user.id, basicInfo?.gender]);

  // Update dietary preferences
  const updateDietaryPreferences = useCallback(async (preferences: {
    tastePreference?: string[];
    cuisinePreference?: string[];
    dislikedIngredients?: string[];
    allergens?: string[];
    dietaryRestrictions?: string[];
    mealFrequency?: number;
    snackPreference?: boolean;
    cookingSkillLevel?: string;
  }, showToastMessage: (msg: string) => void) => {
    try {
      const userId = user.id;
      await dataService.updateUserDietaryPreferences(userId, preferences);
      setDietaryPreferences(prev => prev ? { ...prev, ...preferences } : { userId, ...preferences } as any);
      showToastMessage('饮食偏好已更新');
    } catch (error) {
      console.error('Failed to update dietary preferences:', error);
      showToastMessage('更新失败，请重试');
    }
  }, [user.id]);

  // Update nutrition goals
  const updateNutritionGoals = useCallback(async (goals: {
    targetCalories?: number;
    targetProtein?: number;
    targetFat?: number;
    targetCarbs?: number;
    targetFiber?: number;
    goalType?: string;
  }, showToastMessage: (msg: string) => void) => {
    try {
      const userId = user.id;
      await dataService.updateUserNutritionGoals(userId, goals);
      setNutritionGoals(prev => prev ? { ...prev, ...goals } : { userId, ...goals } as any);
      showToastMessage('营养目标已更新');
    } catch (error) {
      console.error('Failed to update nutrition goals:', error);
      showToastMessage('更新失败，请重试');
    }
  }, [user.id]);

  return {
    user,
    setUser,
    inventory,
    setInventory,
    logs,
    setLogs,
    basicInfo,
    bodyData,
    dietaryPreferences,
    nutritionGoals,
    awardXP,
    handleProfileUpdate,
    handleToggleTheme,
    handleSaveBatch,
    setFavoriteRecipeIds,
    setFavoriteIngredientIds,
    updateBasicInfo,
    updateBodyData,
    updateDietaryPreferences,
    updateNutritionGoals,
    loadUserProfile,
  };
};
